/**
 * Every module's catalogues. A **leaf**: JSON imports only, no components.
 *
 * `i18n/resources.ts` spreads these into `en` and `cs`, so a module's namespace
 * is part of `typeof en` and its keys are type-checked exactly like core's.
 * That is why this file must import nothing else — `resources.ts` is loaded
 * before any component, and a component import here would pull a module's
 * pages, and through them `t()`, into the initialisation of `t()` itself.
 *
 * A module's namespace is its key.
 */
import enMessaging from './messaging/locales/en/messaging.json';
import csMessaging from './messaging/locales/cs/messaging.json';
import enCrm from './crm/locales/en/crm.json';
import csCrm from './crm/locales/cs/crm.json';
import enCurrency from './currency/locales/en/currency.json';
import csCurrency from './currency/locales/cs/currency.json';
import enRocketchat from './rocketchat/locales/en/rocketchat.json';
import csRocketchat from './rocketchat/locales/cs/rocketchat.json';
import enBoard from './board/locales/en/board.json';
import csBoard from './board/locales/cs/board.json';
import enCoffee from './coffee/locales/en/coffee.json';
import csCoffee from './coffee/locales/cs/coffee.json';

export const MODULE_LOCALES = {
  en: { messaging: enMessaging, crm: enCrm, currency: enCurrency, rocketchat: enRocketchat, board: enBoard, coffee: enCoffee },
  cs: { messaging: csMessaging, crm: csCrm, currency: csCurrency, rocketchat: csRocketchat, board: csBoard, coffee: csCoffee },
} as const;

export type ModuleNamespace = keyof typeof MODULE_LOCALES.en;

/**
 * The same namespaces, as a **literal tuple**.
 *
 * i18next types an `ns:key` lookup only for namespaces the component asked for,
 * and only when the list it asked with is a tuple of literals — so the sidebar,
 * which resolves module menu labels, spreads this into its `useTranslation`.
 * Written out rather than derived from `MODULE_LOCALES` because a tuple cannot
 * be derived from object keys; the two checks below make a mismatch a compile
 * error in either direction.
 */
export const MODULE_NAMESPACES = ['messaging', 'crm', 'currency', 'rocketchat', 'board', 'coffee'] as const satisfies readonly ModuleNamespace[];

/** Fails to compile if a catalogue above is missing from the tuple. */
export type ModuleNamespacesComplete = Exclude<ModuleNamespace, (typeof MODULE_NAMESPACES)[number]> extends never
  ? true
  : ['MODULE_NAMESPACES is missing', Exclude<ModuleNamespace, (typeof MODULE_NAMESPACES)[number]>];
const complete: ModuleNamespacesComplete = true;
void complete;
